import { inject, Injectable } from '@angular/core';
import { subject } from '@casl/ability';

import { CaslAction, CaslSubject, StatisticsType } from '@malou-io/package-utils';

import { AbilitiesContext } from '#core/context/abilities.context';
import { SessionStore } from '#core/store/session.store';

/**
 * The order the statistics tabs are displayed in, and therefore the order a redirection falls back through.
 */
const STATISTICS_TYPES_IN_DISPLAY_ORDER: StatisticsType[] = [
    StatisticsType.SEO,
    StatisticsType.E_REPUTATION,
    StatisticsType.SOCIAL,
    StatisticsType.BOOSTERS,
    StatisticsType.ROI,
];

// Answers whether the current user may read a statistics area of a restaurant.
//
// It asks `AbilitiesContext`, which holds the rules the backend built and shipped through `init-session`, so this verdict and the one the API enforces are the same rule rather than two implementations of it.
//
// A Malou admin reads every area of every restaurant.
// The API lets an admin through each statistics route whatever their userrestaurants, while init-session ships them rules that reach only the restaurants they are linked to, so the sidebar and the guards would otherwise hide pages the API serves.
// The bypass is confined to statistics on purpose: other permission services keep the shipped rules, since parts of the app must not follow the admin status.
//
// Every answer is meaningless until `AbilitiesContext.areAbilitiesLoaded` turns true: the abilities start empty, so they deny everything, and a caller acting on a negative answer — a redirection, most of all — must check that flag first.
//
// Provided by `provideLoggedInScope()`, like `CampaignPermissionsService`: `AbilitiesContext` lives in that injector, so a root-provided service would be created above it and find nothing.
@Injectable()
export class StatisticsPermissionsService {
    private readonly _abilitiesContext = inject(AbilitiesContext);
    private readonly _sessionStore = inject(SessionStore);

    canRead(restaurantId: string, type: StatisticsType): boolean {
        if (this._sessionStore.user()?.isAdmin()) {
            return true;
        }
        return this._abilitiesContext
            .userRestaurantAbilities()
            .can(CaslAction.READ, subject(CaslSubject.STATISTICS, { restaurantId, type }));
    }

    /** Whether any statistics area of this restaurant is readable, which is what decides if the parent entry is worth showing. */
    canReadAny(restaurantId: string): boolean {
        return STATISTICS_TYPES_IN_DISPLAY_ORDER.some((type) => this.canRead(restaurantId, type));
    }

    /**
     * Whether the area is readable on at least one of these restaurants.
     * The aggregated pages are not scoped to a restaurant, and they narrow their selection server-side, so a single readable restaurant is enough to make the tab worth showing.
     */
    canReadForAnyRestaurant(restaurantIds: string[], type: StatisticsType): boolean {
        return restaurantIds.some((restaurantId) => this.canRead(restaurantId, type));
    }

    /** The aggregated counterpart of `getFirstReadableType`. */
    getFirstReadableTypeForAnyRestaurant(restaurantIds: string[]): StatisticsType | null {
        return STATISTICS_TYPES_IN_DISPLAY_ORDER.find((type) => this.canReadForAnyRestaurant(restaurantIds, type)) ?? null;
    }

    /** The area a user landing on a forbidden tab should be sent to, or null when none is readable. */
    getFirstReadableType(restaurantId: string, candidates: StatisticsType[] = STATISTICS_TYPES_IN_DISPLAY_ORDER): StatisticsType | null {
        return candidates.find((type) => this.canRead(restaurantId, type)) ?? null;
    }
}
